import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from './firebase';

export const getSupplierPerformanceMetrics = async (supplierId, category = null) => {
  try {
    // Simplified query - just get by supplierId first
    let ratingsQuery = query(
      collection(db, 'supplierRatings'),
      where('supplierId', '==', supplierId)
    );

    const ratingsSnapshot = await getDocs(ratingsQuery);
    let ratings = ratingsSnapshot.docs.map(doc => doc.data());

    // Client-side filtering for category if specified
    if (category && ratings.length > 0) {
      ratings = ratings.filter(r => r.category === category);
    }

    // Client-side sorting by date
    ratings.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA;
    });

    if (ratings.length === 0) {
      return {
        hasHistory: false,
        projectCount: 0,
        averageRating: 0,
        categoryAverages: {},
        recentTrend: 'neutral',
        recommendationRate: 0,
        lastRating: null,
        strengthAreas: [],
        improvementAreas: []
      };
    }

    // Calculate overall metrics
    const projectCount = ratings.length;
    const overallRatings = ratings.map(r => r.ratings.overall).filter(r => r > 0);
    const averageRating = overallRatings.length > 0 
      ? overallRatings.reduce((sum, rating) => sum + rating, 0) / overallRatings.length 
      : 0;

    // Calculate category averages
    const categories = ['qualityOfWork', 'timeliness', 'communication', 'priceValue', 'professionalism'];
    const categoryAverages = {};
    
    categories.forEach(cat => {
      const categoryRatings = ratings
        .map(r => r.ratings[cat])
        .filter(r => r > 0);
      
      categoryAverages[cat] = categoryRatings.length > 0
        ? categoryRatings.reduce((sum, rating) => sum + rating, 0) / categoryRatings.length
        : 0;
    });

    // Calculate recent trend (last 3 vs previous ratings)
    let recentTrend = 'neutral';
    if (ratings.length >= 3) {
      const recent3 = ratings.slice(0, 3).map(r => r.ratings.overall);
      const older = ratings.slice(3).map(r => r.ratings.overall);
      
      const recentAvg = recent3.reduce((sum, r) => sum + r, 0) / recent3.length;
      const olderAvg = older.length > 0 
        ? older.reduce((sum, r) => sum + r, 0) / older.length 
        : recentAvg;

      if (recentAvg > olderAvg + 0.3) recentTrend = 'improving';
      else if (recentAvg < olderAvg - 0.3) recentTrend = 'declining';
    }

    // Calculate recommendation rate
    const recommendationRate = ratings.length > 0
      ? (ratings.filter(r => r.wouldRecommend).length / ratings.length) * 100
      : 0;

    // Identify strength and improvement areas
    const sortedCategories = Object.entries(categoryAverages)
      .sort(([,a], [,b]) => b - a);
    
    const strengthAreas = sortedCategories.slice(0, 2).map(([cat]) => cat);
    const improvementAreas = sortedCategories.slice(-2).map(([cat]) => cat);

    return {
      hasHistory: true,
      projectCount,
      averageRating: parseFloat(averageRating.toFixed(2)),
      categoryAverages,
      recentTrend,
      recommendationRate: parseFloat(recommendationRate.toFixed(1)),
      lastRating: ratings[0],
      strengthAreas,
      improvementAreas
    };

  } catch (error) {
    console.error('Error fetching supplier performance metrics:', error);
    return {
      hasHistory: false,
      projectCount: 0,
      averageRating: 0,
      categoryAverages: {},
      recentTrend: 'neutral',
      recommendationRate: 0,
      lastRating: null,
      strengthAreas: [],
      improvementAreas: []
    };
  }
};

export const getSimilarProjectPerformance = async (supplierId, projectCategory, taskCategory) => {
  try {
    // Simplified query - just get by supplierId
    const similarQuery = query(
      collection(db, 'supplierRatings'),
      where('supplierId', '==', supplierId)
    );

    const ratingsSnapshot = await getDocs(similarQuery);
    let ratings = ratingsSnapshot.docs.map(doc => doc.data());

    // Client-side filtering for task category if specified
    if (taskCategory && ratings.length > 0) {
      ratings = ratings.filter(r => r.category === taskCategory);
    }

    // Client-side sorting and limiting
    ratings.sort((a, b) => {
      const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(a.createdAt);
      const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
      return dateB - dateA;
    });
    
    // Limit results
    ratings = ratings.slice(0, taskCategory ? 5 : 10);

    if (ratings.length === 0) {
      return {
        hasSimilarWork: false,
        similarProjectCount: 0,
        averageRatingForSimilar: 0
      };
    }

    const averageRatingForSimilar = ratings.length > 0
      ? ratings.reduce((sum, r) => sum + r.ratings.overall, 0) / ratings.length
      : 0;

    return {
      hasSimilarWork: true,
      similarProjectCount: ratings.length,
      averageRatingForSimilar: parseFloat(averageRatingForSimilar.toFixed(2)),
      recentSimilarProjects: ratings.slice(0, 3).map(r => ({
        projectName: r.projectName,
        rating: r.ratings.overall,
        date: r.createdAt,
        feedback: r.feedback
      }))
    };

  } catch (error) {
    console.error('Error fetching similar project performance:', error);
    return {
      hasSimilarWork: false,
      similarProjectCount: 0,
      averageRatingForSimilar: 0
    };
  }
};

export const calculateQuoteScore = (quote, supplierPerformance, taskCategory) => {
  let score = 0;

  // Price score (40 points max) - needs to be calculated with all quotes
  // This will be handled in the comparison component

  // Timeline score (20 points max) - Category-specific
  if (quote.timelineDays) {
    const category = taskCategory?.toLowerCase();
    
    if (category === 'upholstery') {
      // Upholstery timeline scale (months-based)
      if (quote.timelineDays <= 30) score += 20;      // 1 month or less
      else if (quote.timelineDays <= 60) score += 18;  // 2 months
      else if (quote.timelineDays <= 90) score += 15;  // 3 months
      else if (quote.timelineDays <= 120) score += 12; // 4 months
      else if (quote.timelineDays <= 150) score += 8;  // 5 months
      else if (quote.timelineDays <= 180) score += 5;  // 6 months
      else score += 2; // Over 6 months
    } else {
      // Default/Lighting timeline scale
      if (quote.timelineDays <= 7) score += 20;       // 1 week
      else if (quote.timelineDays <= 14) score += 18;  // 2 weeks
      else if (quote.timelineDays <= 21) score += 15;  // 3 weeks
      else if (quote.timelineDays <= 30) score += 12;  // 1 month
      else if (quote.timelineDays <= 45) score += 8;   // 6 weeks
      else score += 5; // Over 6 weeks
    }
  }

  // Historical performance score (25 points max)
  if (supplierPerformance.hasHistory) {
    const performanceMultiplier = supplierPerformance.averageRating / 5; // 0-1 multiplier
    score += 25 * performanceMultiplier;
    
    // Bonus for similar work experience
    if (supplierPerformance.similarWork && supplierPerformance.similarWork.hasSimilarWork) {
      score += 5; // Bonus for relevant experience
    }
    
    // Trend bonus/penalty
    if (supplierPerformance.recentTrend === 'improving') score += 3;
    else if (supplierPerformance.recentTrend === 'declining') score -= 3;
  } else {
    // New supplier gets middle score
    score += 12;
  }

  // Quote completeness and quality (15 points max)
  if (quote.description && quote.description.length > 50) score += 5;
  if (quote.materials && quote.materials.length > 20) score += 5;
  if (quote.notes && quote.notes.length > 10) score += 2;
  if (quote.timelineDays > 0) score += 3; // Provided specific timeline

  return Math.min(Math.round(score), 100); // Cap at 100
};

export const formatCategoryName = (category) => {
  const categoryNames = {
    qualityOfWork: 'Quality of Work',
    timeliness: 'Timeliness',
    communication: 'Communication', 
    priceValue: 'Price & Value',
    professionalism: 'Professionalism'
  };
  return categoryNames[category] || category;
};

export const getPerformanceBadge = (averageRating) => {
  if (averageRating >= 4.5) return { label: 'Excellent', color: 'green', bgColor: 'bg-green-100', textColor: 'text-green-800' };
  if (averageRating >= 4.0) return { label: 'Very Good', color: 'blue', bgColor: 'bg-blue-100', textColor: 'text-blue-800' };
  if (averageRating >= 3.5) return { label: 'Good', color: 'yellow', bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' };
  if (averageRating >= 3.0) return { label: 'Average', color: 'gray', bgColor: 'bg-gray-100', textColor: 'text-gray-800' };
  return { label: 'Below Average', color: 'red', bgColor: 'bg-red-100', textColor: 'text-red-800' };
};